import { Eye } from 'lucide-react';
import styled from 'styled-components';

import { IconButton } from '../../../../../../components/atoms/icon-button/icon-button';
import { ClienteProdutoListItem } from '../../../../../../types/cliente-produto';
import { formatPreco, simNao } from './format';
import { ProdutoStatusBadge } from './produto-status-badge';
import { ProdutoTipoBadge } from './produto-tipo-badge';

interface Props {
  items: ClienteProdutoListItem[];
  onOpen: (id: string) => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) => theme.colors.bgMuted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: 600;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-weight: 600;
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
  }
`;

const SrOnly = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const ProdutosTable = ({ items, onOpen }: Props) => (
  <Table>
    <SrOnly>Produtos do cliente</SrOnly>
    <thead>
      <tr>
        <th scope="col">Nome</th>
        <th scope="col">Tipo</th>
        <th scope="col">Preço</th>
        <th scope="col">Recorrente</th>
        <th scope="col">Status</th>
        <th scope="col">Ações</th>
      </tr>
    </thead>
    <tbody>
      {items.map((produto) => (
        <tr key={produto.id} data-testid={`produto-row-${produto.id}`}>
          <td>
            <NameCell>
              <strong>{produto.nome || '—'}</strong>
              {produto.categoriaDescricao && (
                <small>{produto.categoriaDescricao}</small>
              )}
            </NameCell>
          </td>
          <td>
            <ProdutoTipoBadge
              tipo={produto.tipo}
              descricao={produto.tipoDescricao}
            />
          </td>
          <td>{formatPreco(produto.preco, produto.moedaSigla)}</td>
          <td>{simNao(produto.recorrencia)}</td>
          <td>
            <ProdutoStatusBadge ativo={produto.ativo} />
          </td>
          <td>
            <IconButton
              label={`Ver detalhes de ${produto.nome || produto.id}`}
              onClick={() => onOpen(produto.id)}
            >
              <Eye size={16} />
            </IconButton>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
